import { useEffect, useRef } from 'react';
import { useQueryState } from 'hooks/useQueryState';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { QueueView, QueueViewCountsResponse } from '@hone-automation/common';

type FilterCounts = QueueViewCountsResponse;

interface UseDefaultFilterOptions {
  filterCounts: FilterCounts;
  activeView: QueueView | undefined;
  onSetView: (view: QueueView | undefined) => void;
  /** Whether request data has been loaded (prevents waiting forever on empty queues) */
  dataLoaded?: boolean;
}

/**
 * Picks a default view when none is set:
 * - wait for deeplinks to finish
 * - respect an explicit view from the URL
 * - fall back by priority: Blockers → Me → My Team → Client
 */
export const useDefaultFilter = ({ filterCounts, activeView, onSetView, dataLoaded }: UseDefaultFilterOptions) => {
  const [urlView] = useQueryState('view');
  const { isDeepLinkPending } = useQueueContext();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (isDeepLinkPending) return;

    const totalItems = filterCounts.Blockers + filterCounts.Me + filterCounts['My Team'] + filterCounts.Client;
    const hasItems = totalItems > 0 || filterCounts.Completed > 0;

    if (!hasItems && !dataLoaded) return;

    hasInitialized.current = true;

    if (urlView && urlView !== 'Groups') return;
    if (activeView) return;

    const priority: QueueView[] = ['Blockers', 'Me', 'My Team', 'Client'];
    const selectedView = priority.find(view => (filterCounts[view] as number) > 0);

    if (selectedView) onSetView(selectedView);
  }, [filterCounts, urlView, isDeepLinkPending, onSetView, dataLoaded, activeView]);

  /**
   * Returns true if there are no items in any active filter (excluding Completed)
   * Used to show empty state in BrowserPanel
   */
  const hasNoActiveItems =
    filterCounts.Blockers === 0 && filterCounts.Me === 0 && filterCounts['My Team'] === 0 && filterCounts.Client === 0;

  return { hasNoActiveItems };
};
